/**
 * Theme Configuration Types
 *
 * Defines the structure for map themes including layer styles,
 * POI appearance, and category configurations.
 */

// Layer types from DB enum. NOTE: this is the strict set the API/DB will accept
// for `layer.layerType`. Theme-only keys live on `ThemeLayerKey` below so the
// editor can target visual variants (e.g. plaza) without polluting DB validation.
export type LayerType =
  // Polygon/Line layers
  | "boundary"
  | "path_primary"
  | "path_secondary"
  | "path_tertiary"
  | "path_wide"
  | "bridge"
  | "tunnel"
  | "building"
  | "building_primary" // Important landmarks, restaurants, main attractions
  | "building_secondary" // Background structures, vendors, service buildings
  | "building_entrance" // Entrance buildings, gates
  | "barrier"
  | "vegetation"
  | "water"
  | "waterway"
  | "landscape"
  | "rail"
  | "bus"
  | "parking"
  | "zone"
  | "custom_polygon"
  | "custom_path"
  // Special
  | "vignette"
  | "prop";

/**
 * Theme-only key for the `path_wide` polygon variant ("plaza walkable").
 * `path_wide` line geometry → `theme.layers.path_wide` (line tokens).
 * `path_wide` Polygon geometry → `theme.layers.plaza` (fill tokens).
 * Independent visual editing in the theme editor; same DB layer_type.
 */
export type ThemeLayerKey = LayerType | "plaza";

// POI categories - now dynamic from DB, any slug allowed
export type PoiCategory = string;

// POI visibility tier (1 = most important, 3 = least)
export type PoiTier = 1 | 2 | 3;

// Zoom-dependent stroke width configuration
export interface StrokeWidthZoom {
  minZoom: number; // zoom level for minimum width
  maxZoom: number; // zoom level for maximum width
  minWidth: number; // stroke width at minZoom
  maxWidth: number; // stroke width at maxZoom
}

// Depth effect for pseudo-3D shadow on polygons (Disney map technique)
export interface DepthEffect {
  enabled: boolean;
  shadowColor?: string; // defaults to darker version of fill
  shadowOffset?: number; // meters offset (default 2). All consumers use baked shadow geometry.
  shadowOpacity?: number; // 0-1 (default 0.3)
  shadowMinZoom?: number; // zoom level below which shadows fade out (default 15)
}

// Layer style configuration
export interface LayerStyle {
  fill?: string;
  fillOpacity?: number;
  lineOpacity?: number;
  stroke?: string;
  strokeWidth?: number;
  strokeWidthZoom?: StrokeWidthZoom; // zoom-dependent width (overrides strokeWidth)
  strokeDasharray?: number[];
  zOrder: number;
  texture?: string; // texture slug or null for solid color
  strokeTexture?: string; // texture slug for line patterns (paths, rails, etc.)
  depthEffect?: DepthEffect; // pseudo-3D shadow effect for polygons
  lineCap?: "round" | "butt" | "square"; // Line cap style (default: round, or butt when dashed)
  casing?: { enabled: boolean; color?: string; width?: number }; // Casing: wider line underneath. width = additional px per side
  smooth?: boolean; // Chaikin smoothing for line/polygon layers (applied at publish)
  smoothIterations?: number; // Chaikin iterations 1-4 (default 2)
  cornerRadius?: number; // absolute corner radius in meters for buildings (default 1.5)
}

// POI category configuration
export interface PoiCategoryConfig {
  displayName: string;
  icon: string;
  tier: PoiTier;
  visible: boolean;
  circleColor?: string; // Per-category circle color override
}

// POI base style (consistent across all POIs)
export interface PoiStyle {
  circleRadius: number;
  circleColor: string;
  circleStrokeColor: string;
  circleStrokeWidth: number;
  iconSize: number;
  iconColor: string;
  // Shadow properties for sprite generation
  shadowColor: string;
  shadowBlur: number;
  shadowOffsetY: number; // offset magnitude (applied via shadowDirection)
  shadowDirection?: ShadowDirection; // independent from global building shadow direction
}

// Zoom thresholds by tier
export interface PoiMinZoom {
  1: number;
  2: number;
  3: number;
}

// Global shadow direction (single light source for all shadows)
export interface ShadowDirection {
  x: number; // -1 (left) to 1 (right), default 1
  y: number; // -1 (up) to 1 (down), default 1
}

// Zoom-dependent shadow offset for sprites (hardcoded z16-z20 range)
export interface ShadowOffsetZoom {
  farOffset: number; // Offset at z16 (zoomed out)
  closeOffset: number; // Offset at z20 (zoomed in)
}

// Shadow effect for props (blurred circle shadow beneath icons)
export interface PropShadow {
  enabled: boolean;
  offset?: number; // Pixels offset down-right at z18 (default 2)
  offsetZoom?: ShadowOffsetZoom; // Zoom-dependent offset (overrides offset)
  opacity?: number; // 0-1 (default 0.3)
  blur?: number; // 0-1 blur amount (default 0.5) - NOT USED by Deck.gl mask shadows
  scale?: number; // Shadow size relative to icon (default 0.8) - NOT USED by Deck.gl mask shadows
}

/**
 * @deprecated Use shadowDirection for direction and per-item PropShadow for enable/opacity.
 * Kept for backwards compatibility with existing themes.
 */
export interface SceneryShadow {
  enabled: boolean;
  offsetX?: number; // Pixels offset right at z18 (default 3, negative = left)
  offsetY?: number; // Pixels offset down at z18 (default 3, negative = up)
  opacity?: number; // 0-1 (default 0.3)
}

// Prop configuration (scatter/stamp decorative elements)
export interface PropConfig {
  icon: string; // prop-tree, prop-palm, layer-bench, etc.
  iconSize: number; // 0.02-0.12 multiplier for sprite
  iconOpacity: number; // 0-1
  zOrder?: number; // Render order (higher = on top), default 7.5
  minZoom?: number; // Minimum zoom level to display this prop
  shadow?: PropShadow; // Ground shadow beneath prop
}

// Gradient easing curve for vignette fog-of-war effect
export type GradientCurve = "linear" | "ease-in" | "ease-out";

// Vignette (boundary mask) configuration
export interface VignetteConfig {
  opacity: number; // 0-1, controls mask transparency
  gradientSteps?: number; // 3-6, number of gradient rings (default 4)
  gradientWidth?: number; // Width of gradient in meters (default 50)
  gradientCurve?: GradientCurve; // Easing curve for opacity falloff (default 'ease-out')
}

// POI name label rendering config (text under POI icon, auto-derived from POI name).
// Default ENABLED in plan 118 (option A locked 2026-05-08). Per-POI override on `pois.show_name_label`.
//
// Combined into the `pois-tier1/2/3` symbol layers so tier minzooms govern both
// icon and label visibility. Halo (soft, via baked text-halo-blur) provides
// readability over varied backgrounds. Drop-shadow attempted via parallel
// shadow-tier layers (commit 0913) but rolled back — independent collision
// pipelines caused orphan shadows rendering without the main label/icon.
// Revisit if/when an offset-shadow look becomes a hard requirement.
export type PoiLabelPlacement = "below" | "right" | "left";

export interface PoiLabelConfig {
  enabled: boolean; // global toggle — default true
  placement: PoiLabelPlacement; // text position relative to icon — default "below"
  fontSize: number; // px
  color: string; // hex (text fill)
  haloColor: string; // rgba/hex (halo fill — typically semi-transparent white)
  haloWidth: number; // px (text-halo-width)
}

// Sponsor ring style per tier (gold/silver/bronze)
export interface SponsorRingStyle {
  color: string; // Ring stroke color
  strokeWidth: number; // Ring stroke width (SVG-space, ~2-5)
  glowBlur: number; // Glow spread (SVG-space stdDeviation, 0-5)
  glowOpacity: number; // Glow intensity (0-1)
  radiusRatio: number; // Ring center / POI circle radius (1.0-1.5)
}

// Complete theme configuration
export interface ThemeConfig {
  // Global colors
  colors: {
    primary: string;
    background: string; // Map canvas color
    maskColor: string; // Vignette/fog-of-war color
  };

  // Vignette configuration
  vignette: VignetteConfig;

  // Layer styles keyed by ThemeLayerKey (LayerType + theme-only keys like "plaza").
  layers: Record<ThemeLayerKey, LayerStyle>;

  // POI base style
  poi: PoiStyle;

  // POI category configurations
  poiCategories: Record<PoiCategory, PoiCategoryConfig>;

  // Zoom thresholds by tier
  poiMinZoom: PoiMinZoom;

  // Prop configurations (scatter/stamp decorative elements)
  props?: Record<string, PropConfig>;

  // Global scenery shadow settings (single light source)
  sceneryShadow?: SceneryShadow;

  // Global shadow direction (affects all shadow layers)
  shadowDirection?: ShadowDirection;

  // Number of shadow layers per polygon (1 = full only, 2 = full + mid for smoother gradient)
  shadowLayers?: 1 | 2;

  // Sponsor ring styles per tier
  sponsorRings?: {
    gold: SponsorRingStyle;
    silver: SponsorRingStyle;
    bronze: SponsorRingStyle;
  };

  // POI name labels (text under icon). Default off — added in plan 114.
  poiLabel?: PoiLabelConfig;

  // When true, this map's `colors.primary` overrides the org `brandColor` as the
  // visitor accent on this map only (PR5). Default false — org brand color wins.
  useAsVisitorAccent?: boolean;

  // Plan 120 v2 — theme-level globals for custom map stickers. The shadow
  // toggle is the only key with admin UI today; opacity/offset multiplier
  // sit here ready for future controls. Per-instance overrides on
  // `map_illustrations` (rotation, width, opacity, zoom range) keep their
  // existing semantic — they win over theme defaults at render time.
  stickers?: {
    /** Default true. Off → admin + visitor skip the paired silhouette IconLayer. */
    shadowsEnabled?: boolean;
    /** Default 0.3. Multiplied with per-instance opacity at render time. */
    shadowOpacity?: number;
    /** Default 1. Scales `STICKER_SHADOW_DEFAULT_OFFSET_PX`. 0 = flat, 2 = exaggerated. */
    shadowOffsetMultiplier?: number;
    /**
     * Theme-wide zoom-range floor for sticker visibility (PR9). Used as
     * fallback when an instance's `min_zoom` column is NULL — the override
     * checkbox in `StickerEditPanel` opts a single sticker out of this
     * default. Defaults to schema's pre-PR9 NOT NULL default of 14.
     */
    defaultMinZoom?: number;
    /** Theme-wide zoom-range ceiling. Default 22. */
    defaultMaxZoom?: number;
  };
}

// Fallback styling for uncategorized POIs
export const UNCATEGORIZED_CATEGORY_STYLE: PoiCategoryConfig & { slug: string; color: string } = {
  slug: "uncategorized",
  displayName: "Uncategorized",
  icon: "map-pin",
  color: "#6B7280",
  tier: 3,
  visible: true,
};

/**
 * Dynamic category from API (poi_categories table)
 * Used by createTieredPOILayers to build layers from database categories
 */
export interface DynamicCategory {
  id: string;
  slug: string;
  name: string;
  icon: string;
  color: string;
  sortOrder: number;
  isSystem: boolean;
  isVisible: boolean;
  isFilterable: boolean;
}

// Partial theme for customizations (deep partial)
export type ThemeCustomizations = {
  colors?: Partial<ThemeConfig["colors"]>;
  layers?: Partial<Record<ThemeLayerKey, Partial<LayerStyle>>>;
  poi?: Partial<PoiStyle>;
  poiCategories?: Partial<Record<PoiCategory, Partial<PoiCategoryConfig>>>;
  poiMinZoom?: Partial<PoiMinZoom>;
  vignette?: Partial<VignetteConfig>;
  props?: Partial<Record<string, Partial<PropConfig>>>;
  sceneryShadow?: Partial<SceneryShadow>;
  shadowDirection?: ShadowDirection;
  shadowLayers?: 1 | 2;
  sponsorRings?: Partial<Record<"gold" | "silver" | "bronze", Partial<SponsorRingStyle>>>;
  useAsVisitorAccent?: boolean;
  poiLabel?: Partial<PoiLabelConfig>;
  stickers?: Partial<NonNullable<ThemeConfig["stickers"]>>;
};

// Base theme metadata (stored in DB)
export interface BaseThemeRecord {
  id: string;
  slug: string;
  name: string;
  config: ThemeConfig;
  createdAt: Date;
}

// Theme record (org customization, stored in DB)
export interface ThemeRecord {
  id: string;
  orgId: string | null;
  baseThemeId: string;
  name: string;
  slug: string;
  customizations: ThemeCustomizations;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}
